import { useCallback, useEffect, useMemo, useState } from "react";
import { addDays, format, startOfDay, startOfWeek, subDays } from "date-fns";
import useApiRequest from "../../../../../../../utils/hooks/useApiRequest";
import useFacility from "../../../../../../../utils/hooks/useFacility";
import getId from "../../../../../../../utils/getId";
import { GET_SWIMMING_LESSONS_TABLE } from "../../../../../../api/apiRoutes";
import { Exercise } from "../../../../../../models/Exercise";
import { Ticket } from "../../../../../../models/Ticket";
import { SwimmingLesson, SwimmingScheduleData } from "../../../../../../models/SwimmingScheduleData";

const WEEK_STARTS_ON = 1 as const;

const toMs = (value: number): number => (value < 1e11 ? value * 1000 : value);

const getWeekStart = (date: Date): Date => startOfWeek(startOfDay(date), { weekStartsOn: WEEK_STARTS_ON });

interface UseChangeSwimmingTermSlotsProps {
  exercise: Exercise;
  ticket: Ticket;
  enabled: boolean;
}

const isLessonAvailable = (
  lesson: SwimmingLesson,
  currentExerciseId: number,
  bookedIds: Set<number>,
  now: number,
  validTo?: number,
): boolean => {
  if (lesson.objectId === currentExerciseId) {
    return false;
  }
  if (bookedIds.has(lesson.objectId)) {
    return false;
  }
  if (lesson.status === "I") {
    return false;
  }
  const startMs = toMs(lesson.startTime);
  if (startMs <= now) {
    return false;
  }
  // Okno sprzedaży zamknięte -> backend i tak odrzuci zapis (komunikat "zajęcie już minęło"
  // mimo przyszłej daty), więc nie pokazujemy takiego terminu jako wybieralnego.
  if (lesson.saleFrom && toMs(lesson.saleFrom) > now) {
    return false;
  }
  if (lesson.saleTo && toMs(lesson.saleTo) <= now) {
    return false;
  }
  if (validTo && startMs > toMs(validTo)) {
    return false;
  }
  const taken = lesson.reserved + (lesson.prereservation ?? 0);
  if (lesson.capacity <= 0 || taken >= lesson.capacity) {
    return false;
  }
  return true;
};

export default function useChangeSwimmingTermSlots({
  exercise,
  ticket,
  enabled,
}: UseChangeSwimmingTermSlotsProps) {
  const facilityIds = useFacility();
  const { get } = useApiRequest();
  const originalWeekStart = useMemo(() => getWeekStart(new Date(toMs(exercise.startTime))), [exercise.startTime]);
  const [anchorDate, setAnchorDate] = useState<Date>(originalWeekStart);
  const [data, setData] = useState<SwimmingScheduleData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  const exerciseGroupIds = getId(exercise.exerciseGroup);
  const dateFrom = useMemo(() => getWeekStart(anchorDate), [anchorDate]);
  const dateTo = useMemo(() => addDays(dateFrom, 6), [dateFrom]);

  const minWeekStart = useMemo(() => {
    const today = startOfDay(new Date());
    const validFrom = ticket.validfrom ? startOfDay(new Date(toMs(ticket.validfrom))) : today;
    const bound = validFrom > today ? validFrom : today;
    return getWeekStart(bound);
  }, [ticket.validfrom]);

  const maxDate = useMemo(
    () => (ticket.validto ? startOfDay(new Date(toMs(ticket.validto))) : undefined),
    [ticket.validto],
  );

  useEffect(() => {
    if (enabled) {
      setAnchorDate(originalWeekStart);
    }
  }, [enabled, originalWeekStart]);

  const fetchLessons = useCallback(async () => {
    if (!enabled || !facilityIds || !exerciseGroupIds) {
      return;
    }

    setLoading(true);
    setError(false);

    const url = GET_SWIMMING_LESSONS_TABLE(
      facilityIds,
      format(dateFrom, "yyyy-MM-dd"),
      format(dateTo, "yyyy-MM-dd"),
      undefined,
      undefined,
      exerciseGroupIds,
    );
    const { result, isError } = await get(url);

    if (!isError && result) {
      setData(result as SwimmingScheduleData);
    } else {
      setData(null);
      setError(true);
    }
    setLoading(false);
  }, [dateFrom, dateTo, enabled, exerciseGroupIds, facilityIds, get]);

  useEffect(() => {
    fetchLessons();
  }, [fetchLessons]);

  const bookedIds = useMemo(
    () => new Set((ticket.exercises || []).map((item) => item.objectId)),
    [ticket.exercises],
  );

  const slots = useMemo(() => {
    const lessons = data?.lessons ?? [];
    const now = Date.now();
    return lessons
      .filter((lesson) => isLessonAvailable(lesson, exercise.objectId, bookedIds, now, ticket.validto))
      .sort((a, b) => toMs(a.startTime) - toMs(b.startTime));
  }, [bookedIds, data?.lessons, exercise.objectId, ticket.validto]);

  const canGoPrevious = dateFrom.getTime() > minWeekStart.getTime();
  const canGoNext = !maxDate || addDays(dateFrom, 7).getTime() <= maxDate.getTime();

  const handlePreviousWeek = () => {
    if (!canGoPrevious) {
      return;
    }
    setAnchorDate((prev) => subDays(getWeekStart(prev), 7));
  };

  const handleNextWeek = () => {
    if (!canGoNext) {
      return;
    }
    setAnchorDate((prev) => addDays(getWeekStart(prev), 7));
  };

  return {
    slots,
    loading,
    error,
    dateFrom,
    dateTo,
    canGoPrevious,
    canGoNext,
    handlePreviousWeek,
    handleNextWeek,
    refetch: fetchLessons,
  };
}
